/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import '../../styles/Projects.css';

const PastProjects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setError('Failed to fetch facilities');
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedFacility === 'all' 
        ? '/api/projects/past'
        : `/api/projects/past?facility=${selectedFacility}`;
      
      const response = await axios.get(url);
      setProjects(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching past projects:', error);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchFacilities();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleView = (project) => {
    navigate(`/projects/view/${project._id}`, { state: { project } });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return '#4caf50'; // Green
      case 'Terminated':
        return '#f44336'; // Red
      case 'Expired':
        return '#ff9800'; // Orange
      default:
        return '#757575'; // Grey
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        {error}
      </Box>
    );
  }

  return (
    <div className="past-projects">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Past Projects
        </Typography>
        
        <FormControl sx={{ minWidth: 200, mb: 3 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 650 }} aria-label="projects table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Project Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Facility</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>PI</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.length > 0 ? (
              projects.map(project => (
                <TableRow 
                  key={project._id}
                  sx={{ '&:hover': { backgroundColor: '#f8f8f8' } }}
                >
                  <TableCell>{project.project_title}</TableCell>
                  <TableCell>{project.facility?.facility_name}</TableCell>
                  <TableCell>
                    {project.project_start_date ? new Date(project.project_start_date).toLocaleDateString() : 'Not set'}
                  </TableCell>
                  <TableCell>
                    {project.project_end_date ? new Date(project.project_end_date).toLocaleDateString() : 'Not set'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={project.project_status}
                      sx={{
                        backgroundColor: getStatusColor(project.project_status),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {project.users?.find(user => user.role === 'PI')?.first_name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleView(project)}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0'
                        }
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" color="text.secondary">
                    No past projects found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PastProjects; 